import calculatorSmallIcon from "@assets/images/calculator-small.svg";
import documentDownload from "@assets/images/document-download.svg";
import documentDuplicate from "@assets/images/document-duplicate.svg";
import mailFilled from "@assets/images/mail-filled.svg";
import pencilIcon from "@assets/images/pencil.svg";
import plusTealIcon from "@assets/images/plus-teal.svg";
import saveIcon from "@assets/images/save-icon.svg";
import saveDraft from "@assets/images/save.svg";
import { Button, Paper, Tooltip } from "@material-ui/core";
import DecimalFormat from "@saleor/components/DecimalFormat";
import DialogConfirmItem from "@saleor/components/DialogConfirmItem";
import MyTypography from "@saleor/components/MyTypography";
import PriceDialogProcessing from "@saleor/components/priceCalcDialogProcessing";
import { ITEM_STATUS } from "@saleor/createOrChangeNotRefContract/constants";
import { getIndexByItemNo } from "@saleor/createOrChangeNotRefContract/helpers";
import { NotRefOrderQueryParams } from "@saleor/createOrChangeNotRefContract/urls";
import {
  priceNotFoundMapping,
  priceNotFoundOrderInformationMapping
} from "@saleor/createOrChangeNotRefContract/utils";
import { ChangeOrderCSTab } from "@saleor/createOrChangeNotRefContract/views/createOrChangeNotRefContractView/types";
import {
  PriceCalculationInput,
  useGetSapPriceCalcMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNotifier from "@saleor/hooks/useNotifier";
import { DeleteIcon } from "@saleor/macaw-ui";
import { shortenLongDecimal } from "@saleor/utils/number";
import classNames from "classnames";
import { FormikProps } from "formik";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import PriceNotFoundPopup from "../DialogPriceNotFound";
import useStyles from "./stylesBottomBar";

interface BottomBarCreateOrChangeNotRefContractProps {
  onAddItem: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  priceData: {
    itemCount: number;
    orderAmtBeforeVat: number;
    orderAmtVat: number;
    orderAmtAfterVat: number;
    currency: string;
    taxClass: string;
  };
  deleteCount: number;
  activeTab: ChangeOrderCSTab;
  onDelete: () => void;
  onSaveDraft: () => void;
  onPreviewOrderTest: () => void;
  onSubmitOrder: () => void;
  onSendMail: () => void;
  onDownloadPdf: () => void;
  onCancelOrder: () => void;
  onDuplicate: () => void;
  onCancelDeleteItem: () => void;
  loading: boolean;
  isDisableBtnCreateOrder: boolean;
  isDisableBtnSaveDraft?: boolean;

  disableAddButton?: boolean;
  disableDeleteButton?: boolean;
  disableCancelItemButton?: boolean;
  disablePriceCalcButton?: boolean;
  disableCancelOrderButton?: boolean;

  formik: FormikProps<any>;
  formikHeader: any;
  orderId?: string;
  params: NotRefOrderQueryParams;
  isViewMode?: boolean;
  isChangeFlow?: boolean;
  currentAction: "none" | "add" | "split" | "edit";
  onCancelAddNewItem: () => void;
  status: string;
  setSubmitState: Dispatch<SetStateAction<any>>;

  onSummaryDetailsChangeComplete?: (values: SummaryDetails) => void;
  onCalculatedPriceComplete?: () => void;
}

export interface SummaryDetails {
  orderAmountBeforeVat: number;
  orderAmountVat: number;
  orderAmountAfterVat: number;
  currency: string;
}

const BottomBarCreateOrChangeNotRefContract: React.FC<BottomBarCreateOrChangeNotRefContractProps> = ({
  onAddItem,
  priceData,
  onDelete,
  onSaveDraft,
  onPreviewOrderTest,
  onSubmitOrder,
  onSendMail,
  onDownloadPdf,
  onDuplicate,
  loading,
  deleteCount,
  activeTab,
  isDisableBtnCreateOrder,
  isDisableBtnSaveDraft,
  disableAddButton,
  disableDeleteButton,
  disableCancelItemButton,
  disablePriceCalcButton,
  disableCancelOrderButton,
  formik,
  formikHeader,
  orderId,
  params,
  isViewMode,
  isChangeFlow,
  onCancelOrder,
  currentAction,
  onEdit,
  onCancelEdit,
  onCancelAddNewItem,
  onCancelDeleteItem,
  setSubmitState,
  status,
  onSummaryDetailsChangeComplete,
  onCalculatedPriceComplete
}) => {
  const [appState] = useAppState();
  const [visible, setVisible] = useState(false);

  const classes = useStyles({
    expandMenu: appState.expandMenu,
    fullScreen: appState.fullScreen,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const notify = useNotifier();
  const { itemCount } = priceData;
  const [openNoPriceFoundModal, setOpenNoPriceFoundModal] = useState(false);

  const TAX_PERCENT = priceData?.taxClass === "0" ? 0 : 0.07;

  const updateLineForDate = (
    lines: Array<
      Pick<
        NotRefOrderLine,
        "bomFlag" | "itemNo" | "parentItemNo" | "requestDate" | "priceDate"
      >
    >
  ) =>
    lines.map(line => {
      const { bomFlag, parentItemNo, priceDate } = line;
      const isBomChildItem = bomFlag && parentItemNo;
      if (!isBomChildItem) {
        return line;
      }
      const parentItem = lines.find(item => item.itemNo === parentItemNo);
      if (!parentItem) {
        return line;
      }
      return {
        ...line,
        priceDate: isChangeFlow ? priceDate : parentItem.priceDate,
        requestDate: parentItem.requestDate
      };
    });

  const getupdatePriceNotFoundVariables = (): {
    id: string;
    input: PriceCalculationInput;
  } => ({
    id: formikHeader.values?.id || "",
    input: {
      lines: priceNotFoundMapping(updateLineForDate([...formik.values.lines])),
      orderInformation: priceNotFoundOrderInformationMapping(
        formikHeader.values,
        orderId,
        isChangeFlow
      )
    }
  });

  const [summaryDetails, setSummaryDetails] = useState<SummaryDetails>({
    orderAmountBeforeVat: 0.0,
    orderAmountVat: 0.0,
    orderAmountAfterVat: 0.0,
    currency: "THB"
  });

  const summaryDetailsString = JSON.stringify(summaryDetails);
  const prevNoPriceFoundModalRef = useRef<boolean>(openNoPriceFoundModal);
  const prevSummaryDetailsChangedRef = useRef<string>(summaryDetailsString);

  useEffect(() => {
    setSummaryDetails({
      orderAmountBeforeVat: priceData?.orderAmtBeforeVat || 0.0,
      orderAmountVat: priceData?.orderAmtVat || 0.0,
      orderAmountAfterVat: priceData?.orderAmtAfterVat || 0.0,
      currency: priceData?.currency || "THB"
    });
  }, [priceData?.orderAmtAfterVat]);

  // Trigger summary details update after price modal closes (supports CashSale)
  useEffect(() => {
    if (!summaryDetails) {
      return;
    }

    const isModalClosing =
      prevNoPriceFoundModalRef.current === true &&
      openNoPriceFoundModal === false;

    prevNoPriceFoundModalRef.current = openNoPriceFoundModal;

    const shouldTriggerCallback = !openNoPriceFoundModal || isModalClosing;
    const hasSummaryChanged =
      summaryDetailsString !== prevSummaryDetailsChangedRef.current;

    if (shouldTriggerCallback && hasSummaryChanged) {
      prevSummaryDetailsChangedRef.current = summaryDetailsString;
      onSummaryDetailsChangeComplete?.(summaryDetails);
    }
  }, [summaryDetailsString, openNoPriceFoundModal]);

  const updatedLinesArray = itemsData =>
    formik?.values?.lines?.map((line, index) => {
      const localIndex = params.asc
        ? index
        : formik?.values?.lines?.length - index - 1;
      if (itemsData?.[localIndex]?.itemNo === line?.itemNo) {
        return {
          ...line,
          pricePerUnit:
            line?.pricePerUnit && line?.materialType === "85"
              ? line?.pricePerUnit || ""
              : itemsData[localIndex]?.pricePerUnit || "",
          netValue:
            line?.pricePerUnit && line?.materialType === "85"
              ? line?.netValue
              : itemsData[localIndex]?.netValue,
          manualPriceFlag: itemsData[localIndex]?.manualPriceFlag,
          materialType: itemsData[localIndex]?.materialType,
          priceStatus: itemsData[localIndex]?.priceStatus,
          priceStatusDesc: itemsData[localIndex]?.priceStatusDesc
        };
      }
      line.pricePerUnit = line?.pricePerUnit || "";
      return line;
    });

  const calculateSummary = lines => {
    const pricePerUnitNumbers = lines.map(item =>
      !item.parentItemNo && item?.itemStatusEn !== "Cancelled (93)"
        ? +item.pricePerUnit * +item.quantity || 0
        : 0
    );

    const orderPerNetValBeforeVat = pricePerUnitNumbers?.reduce(
      (acc, value) => acc + value,
      0
    );

    const orderPerNetValVat = orderPerNetValBeforeVat * TAX_PERCENT;

    const orderAmtAfterVat = orderPerNetValBeforeVat + orderPerNetValVat;

    const summaryDetails: SummaryDetails = {
      orderAmountBeforeVat: orderPerNetValBeforeVat,
      orderAmountVat: orderPerNetValVat,
      orderAmountAfterVat: orderAmtAfterVat,
      currency: "THB"
    };

    return summaryDetails;
  };

  const [
    getPriceData,
    { data: priceDataCalc, loading: loadingPriceData }
  ] = useGetSapPriceCalcMutation({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data?.getPriceCalculation?.success) {
        onCalculatedPriceComplete?.();
      }

      if (data?.getPriceCalculation?.errors?.length) {
        notify({
          status: "error",
          apiMessage: data?.getPriceCalculation.errors[0].message
        });
      }
      const { sapOrderMessages, sapItemMessages } = data.getPriceCalculation;
      if (sapOrderMessages?.length || sapItemMessages?.length) {
        setSubmitState({
          state: "errorPopup",
          context: data.getPriceCalculation
        });
      }

      const price = data?.getPriceCalculation?.response?.orderHeaderOut;

      if (data?.getPriceCalculation?.response?.orderHeaderOut) {
        let summary: SummaryDetails = null;
        if (
          data?.getPriceCalculation?.response?.orderItemsOut.some(
            item => item?.manualPriceFlag === true
          )
        ) {
          summary = calculateSummary(
            updatedLinesArray(
              data?.getPriceCalculation?.response?.orderItemsOut
            )
          );
        } else {
          summary = {
            orderAmountBeforeVat: price?.orderAmountBeforeVat,
            orderAmountVat: price?.orderAmountVat,
            orderAmountAfterVat: price?.orderAmountAfterVat,
            currency: price?.currency
          };
        }
        setSummaryDetails(summary);
      }
      if (data?.getPriceCalculation?.response?.orderItemsOut) {
        formik.setValues(prev => ({
          ...prev,
          lines: updatedLinesArray(
            data?.getPriceCalculation?.response?.orderItemsOut
          )
        }));
      }
    }
  });

  const currentItem = formik?.values?.lines?.[formik.values?.currentIndex];
  useEffect(() => {
    const result = calculateSummary(formik?.values?.lines);
    setSummaryDetails(result);
  }, [currentItem?.quantity, formik?.values?.lines?.length]);

  useEffect(() => {
    if (currentItem?.manualPriceFlag) {
      const result = calculateSummary(formik?.values?.lines);
      setSummaryDetails(result);
    }
  }, [currentItem?.pricePerUnit]);

  const handleNoPriceFoundModal = () => {
    getPriceData({ variables: getupdatePriceNotFoundVariables() });
    setOpenNoPriceFoundModal(true);
  };

  const onConfirmPrice = item => {
    const toUpdatePrice = [...formik.values.lines];
    if (!params.asc) {
      toUpdatePrice.reverse();
    }
    if (item?.lines) {
      item.lines.forEach(line => {
        const index = getIndexByItemNo(toUpdatePrice, line.itemNo);
        if (index >= 0 && index < toUpdatePrice.length) {
          toUpdatePrice[index].pricePerUnit = line?.pricePerUnit;
          toUpdatePrice[index].netValue =
            +line?.pricePerUnit * toUpdatePrice[index].quantity;
        }
      });
    }
    if (!params.asc) {
      toUpdatePrice.reverse();
    }
    formik.setFieldValue("lines", toUpdatePrice);
    setOpenNoPriceFoundModal(false);
  };

  const onCancel = () => setVisible(o => !o);
  const onCancelItem = () => {
    if (currentAction !== "add" && !isViewMode) {
      onCancel();
    } else {
      onCancelAddNewItem();
    }
  };
  const onConfirm = () => {
    setVisible(false);
    onCancelEdit();
  };
  return (
    <div
      className={classes.menu}
      data-testid="bottomBar-createOrChangeNotRefContract"
    >
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({itemCount}{" "}
              {itemCount >= 2
                ? intl.formatMessage({ defaultMessage: "items" })
                : intl.formatMessage({ defaultMessage: "item" })}
              )
            </MyTypography>
          </div>
          <div className={classes.sectionRight}>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                <DecimalFormat
                  value={summaryDetails.orderAmountBeforeVat}
                  isGrayDecimal={false}
                  countDecimal={2}
                />
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(TAX_PERCENT * 100)}%`}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                <DecimalFormat
                  value={summaryDetails.orderAmountVat}
                  isGrayDecimal={false}
                  countDecimal={2}
                />
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography
                variant="subtitle1"
                className={classes.netPriceNumber}
              >
                <DecimalFormat
                  value={summaryDetails.orderAmountAfterVat}
                  isGrayDecimal={false}
                  countDecimal={2}
                />
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {summaryDetails.currency}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <div className={classes.leftContainer}>
            {activeTab === ChangeOrderCSTab.itemDetails && (
              <>
                {!isViewMode && isChangeFlow && currentAction === "add" && (
                  <Button
                    id="cancelSaveDraft"
                    data-testid="cancelSaveDraftButton"
                    variant="outlined"
                    color="inherit"
                    className={classes.btnSaveDraft}
                    disabled={loading}
                    onClick={onCancelItem}
                  >
                    <span>
                      <FormattedMessage defaultMessage="ยกเลิกเพิ่มรายการ" />
                    </span>
                  </Button>
                )}
                {!isViewMode && isChangeFlow && currentAction !== "add" && (
                  <Button
                    id="cancelDeleteItem"
                    data-testid="cancelDeleteItemButton"
                    variant="outlined"
                    color="primary"
                    onClick={onCancelDeleteItem}
                    className={classNames(classes.btnDelete)}
                    disabled={
                      disableCancelItemButton || deleteCount === 0 || loading
                    }
                  >
                    {deleteCount > 1 ? (
                      <FormattedMessage defaultMessage="Cancel items" />
                    ) : (
                      <FormattedMessage defaultMessage="Cancel item" />
                    )}
                    &nbsp;(
                    <MyTypography
                      variant="subtitle2"
                      className={classes.deleteItem}
                    >
                      <b>{deleteCount}</b>
                    </MyTypography>
                    )
                  </Button>
                )}

                {!isViewMode && !isChangeFlow && (
                  <Button
                    id="deleteItems"
                    data-testid="deleteItemsButton"
                    variant="outlined"
                    color="primary"
                    onClick={onDelete}
                    className={classNames(classes.btnDelete)}
                    disabled={
                      disableDeleteButton || deleteCount === 0 || loading
                    }
                  >
                    {deleteCount > 1 ? (
                      <FormattedMessage defaultMessage="Delete items" />
                    ) : (
                      <FormattedMessage defaultMessage="Delete item" />
                    )}
                    &nbsp;(
                    <MyTypography
                      variant="subtitle2"
                      className={classes.deleteItem}
                    >
                      <b>{deleteCount}</b>
                    </MyTypography>
                    )
                  </Button>
                )}
              </>
            )}
            {isViewMode &&
              (status === ITEM_STATUS.RECEIVED_ORDER ||
                status === ITEM_STATUS.PARTIAL_DELIVERY) && (
                <Button
                  id="cancelOrder"
                  data-testid="cancelOrderButton"
                  variant="outlined"
                  color="primary"
                  className={classNames(classes.btnDelete)}
                  startIcon={<DeleteIcon />}
                  disabled={disableCancelOrderButton || loading}
                  onClick={onCancelOrder}
                >
                  <span>
                    <FormattedMessage defaultMessage="ยกเลิกคำสั่งซื้อ" />
                  </span>
                </Button>
              )}

            {!isViewMode &&
              !isChangeFlow &&
              activeTab === ChangeOrderCSTab.itemDetails && (
                <Button
                  id="addItem"
                  data-testid="addItemButton"
                  variant="outlined"
                  color="secondary"
                  className={classes.btnAddProduct}
                  disabled={disableAddButton || loading}
                  startIcon={<InlineSVG src={plusTealIcon} />}
                  onClick={onAddItem}
                >
                  <span>
                    <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
                  </span>
                </Button>
              )}
            {isChangeFlow && isViewMode && (
              <>
                <Tooltip title={"Send Email"} placement={"top"}>
                  <Button
                    id="sendMail"
                    data-testid="sendMailButton"
                    variant={"outlined"}
                    color={"secondary"}
                    disabled={loading}
                    onClick={onSendMail}
                  >
                    <img src={mailFilled} alt="" />
                  </Button>
                </Tooltip>
                <Tooltip title={"Download PDF"} placement={"top"}>
                  <Button
                    id="downloadPDF"
                    data-testid="downloadPDFButton"
                    variant={"outlined"}
                    color={"secondary"}
                    onClick={onDownloadPdf}
                    disabled={loading}
                  >
                    <img src={documentDownload} alt="" />
                  </Button>
                </Tooltip>
              </>
            )}
            {isViewMode && (
              <Tooltip title={"Copy Order"} placement={"top"}>
                <Button
                  id="copyOrder"
                  data-testid="copyOrderButton"
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={onDuplicate}
                  disabled={loading}
                >
                  <img src={documentDuplicate} alt="" />
                </Button>
              </Tooltip>
            )}
          </div>
          <div
            className={classNames(
              classes.leftContainer,
              !isViewMode && isChangeFlow && classes.reversedButtons
            )}
          >
            {isViewMode ? (
              <>
                {activeTab === ChangeOrderCSTab.itemDetails && (
                  <Button
                    id="addItem"
                    data-testid="addItemButton"
                    variant="outlined"
                    color="secondary"
                    onClick={onAddItem}
                    className={classes.btnAddProduct}
                    disabled={disableAddButton || loading}
                    startIcon={<InlineSVG src={plusTealIcon} />}
                  >
                    <span>
                      <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
                    </span>
                  </Button>
                )}
                <Button
                  id="editOrder"
                  data-testid="editOrderButton"
                  variant="contained"
                  color="secondary"
                  className={classes.btnAddProduct}
                  startIcon={<InlineSVG src={pencilIcon} />}
                  disabled={loading}
                  onClick={onEdit}
                >
                  <span>
                    <FormattedMessage defaultMessage="แก้ไขคำสั่งซื้อ" />
                  </span>
                </Button>
              </>
            ) : (
              <>
                {isChangeFlow ? (
                  <Button
                    id="saveOrder"
                    data-testid="saveOrderButton"
                    variant="outlined"
                    color="secondary"
                    className={classNames(
                      classes.btnSaveDraft,
                      !isViewMode && isChangeFlow && classes.btnSaveDraftBg
                    )}
                    startIcon={
                      <InlineSVG
                        src={!isViewMode && isChangeFlow ? saveDraft : saveIcon}
                      />
                    }
                    disabled={
                      isDisableBtnSaveDraft ||
                      (isChangeFlow && currentAction === "split") ||
                      loading
                    }
                    onClick={onSubmitOrder}
                  >
                    <span>
                      {" "}
                      <FormattedMessage defaultMessage="บันทึกคำสั่งซื้อ" />
                    </span>
                  </Button>
                ) : (
                  <Button
                    id="saveDraft"
                    data-testid="saveDraftButton"
                    variant="outlined"
                    color="secondary"
                    className={classNames(
                      classes.btnSaveDraft,
                      !isViewMode && isChangeFlow && classes.btnSaveDraftBg
                    )}
                    startIcon={
                      <InlineSVG
                        src={!isViewMode && isChangeFlow ? saveDraft : saveIcon}
                      />
                    }
                    disabled={isDisableBtnSaveDraft || loading}
                    onClick={onSaveDraft}
                  >
                    <span>
                      {" "}
                      <FormattedMessage defaultMessage="บันทึกฉบับร่าง....." />
                    </span>
                  </Button>
                )}
                {activeTab === ChangeOrderCSTab.itemDetails &&
                  !isViewMode &&
                  isChangeFlow &&
                  currentAction === "add" && (
                    <Button
                      id="addItem"
                      data-testid="addItemButton"
                      variant="outlined"
                      color="secondary"
                      className={classes.btnAddProduct}
                      disabled={disableAddButton || loading}
                      startIcon={<InlineSVG src={plusTealIcon} />}
                      onClick={onAddItem}
                    >
                      <span>
                        <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
                      </span>
                    </Button>
                  )}

                {!isViewMode && isChangeFlow && currentAction !== "add" && (
                  <Button
                    id="cancelEditOrder"
                    data-testid="cancelEditOrderButton"
                    variant="outlined"
                    color="inherit"
                    className={classes.btnSaveDraft}
                    disabled={loading}
                    onClick={onCancel}
                  >
                    <span>
                      <FormattedMessage defaultMessage="ยกเลิกแก้ไขคำสั่งซื้อ" />
                    </span>
                  </Button>
                )}
                {activeTab === ChangeOrderCSTab.itemDetails && (
                  <Button
                    id="calculatePrice"
                    data-testid="calculatePriceButton"
                    variant="outlined"
                    color="secondary"
                    className={classes.btnSaveDraft}
                    disabled={
                      disablePriceCalcButton ||
                      (isChangeFlow && currentAction === "split") ||
                      loading
                    }
                    startIcon={
                      <InlineSVG
                        width={24}
                        height={24}
                        src={calculatorSmallIcon}
                      />
                    }
                    onClick={handleNoPriceFoundModal}
                  >
                    <span>
                      <FormattedMessage defaultMessage="คำนวณราคา" />
                    </span>
                  </Button>
                )}
                {!isViewMode && !isChangeFlow && (
                  <Button
                    id="saveOrder"
                    data-testid="saveOrderButton"
                    variant="contained"
                    color="secondary"
                    className={classes.btnConfirmOrder}
                    disabled={isDisableBtnCreateOrder || loading}
                    onClick={onSubmitOrder}
                  >
                    <FormattedMessage defaultMessage="ตกลงสั่งซื้อ" />
                  </Button>
                )}
                <Button
                  id="previewOrder"
                  data-testid="previewOrderButton"
                  style={{ display: "none" }}
                  variant="outlined"
                  color="secondary"
                  className={classes.btnSaveDraft}
                  disabled={loading}
                  onClick={onPreviewOrderTest}
                >
                  <span>
                    <FormattedMessage defaultMessage="Preview Order Test" />
                  </span>
                </Button>
              </>
            )}
          </div>
          {loadingPriceData ? (
            <PriceDialogProcessing
              open={loadingPriceData}
              description={"ระบบกำลังคำนวณราคา"}
            />
          ) : (
            <PriceNotFoundPopup
              open={openNoPriceFoundModal}
              formik={formik}
              resultData={priceDataCalc}
              orderId={orderId}
              params={params}
              onCancel={() => setOpenNoPriceFoundModal(false)}
              onConfirm={e => onConfirmPrice(e)}
            />
          )}
        </div>
      </Paper>
      <DialogConfirmItem
        onConfirm={onConfirm}
        onClose={() => onCancel()}
        open={visible}
        title={"ยกเลิกแก้ไขคำสั่งซื้อ"}
        content={
          "กรุณากด “ยืนยัน” เพื่อยกเลิกแก้ไขคำสั่งซื้อ หรือกด “กลับไป” เพื่อปิดหน้าต่างนี้"
        }
      />
    </div>
  );
};

export default BottomBarCreateOrChangeNotRefContract;
